import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { formatDateTime } from "@exonn/utils";
import type {
  SupplierCreditResource,
  SupplierProcurementConditionResource,
  SupplierResource,
} from "@exonn/js-sdk";
import { Panel } from "src/components/Panel/Panel.tsx";
import { Flex } from "src/components/Flex.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { Select } from "src/components/Select/Select.tsx";
import { MuiTable, TableCell } from "src/components/MuiTable/MuiTable.tsx";
import { useList } from "src/system/useList.tsx";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";
import { useResourceFormData } from "src/components/Form/hooks/useResourceFormData.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import { useGlobalModuleTabs } from "src/App/useGlobalModuleTabs.ts";
import { FormAction } from "src/components/Form/Form.types.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { serverErrorMessage } from "src/admin/modules/supplier-order/forms/supplier-order/hooks/useServerErrorMessage.ts";

const money = (value: string | undefined) =>
  Number(value ?? 0).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Abrechnung поставщика (задача 5.6): ручной расчёт по Lieferkondition и список Gutschriften / Aufstellungen
 * с документом, суммой и состоянием письма; письмо можно отправить повторно. Права проверяет бэкенд.
 */
export const SupplierCredits = () => {
  const { t } = useResourceFormTranslation();
  const data = useResourceFormData<{ data: SupplierResource }>();
  const supplierId = data?.data?.id ?? "";
  const exonnApi = useExonnApi();
  const snackbar = useSnackbar();
  const { api } = useGlobalModuleTabs();
  const [conditionId, setConditionId] = useState<string | null>(null);
  const [refreshId, setRefreshId] = useState(0);

  const supplierFilter = {
    items: [{ field: "supplier_id", operator: "is", value: supplierId }],
  };
  const {
    query: { data: conditions },
  } = useList<SupplierProcurementConditionResource>({
    resource: ResourceEnum.SupplierProcurementCondition,
    filter: supplierFilter,
  });
  const {
    query: { data: credits },
  } = useList<SupplierCreditResource>({
    resource: "supplier-credit",
    filter: supplierFilter,
    refreshId,
  });

  const action = useMutation({
    mutationFn: ({ url, body }: { url: string; body?: unknown }) =>
      exonnApi.client.post(url, body),
    onSuccess: (_response, { url }) => {
      setRefreshId((id) => id + 1);
      snackbar.success({
        flash: true,
        message: url.endsWith("/run")
          ? t("credits.notifications.run")
          : t("credits.notifications.sent"),
      });
    },
    onError: (error) => {
      snackbar.error({
        flash: true,
        message: serverErrorMessage(error, t("credits.notifications.failed")),
      });
    },
  });

  if (!supplierId) {
    return null;
  }

  // Abrechnung — только по условиям с Gutschrift или Aufstellung.
  const settledConditions = (conditions?.data ?? []).filter(
    (condition) => condition.payment_type !== "supplier_invoice",
  );
  const rows = credits?.data ?? [];

  return (
    <Flex flexDirection={"column"} gap={"15px"} style={{ padding: 15 }}>
      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Panel.Icon>
              <span className={"icon-15-basic_info"} />
            </Panel.Icon>
            {t("credits.run.title")}
          </Panel.Title>
        </Panel.Header>
        <Panel.Content>
          <Flex gap={"10px"} alignItems={"flex-end"}>
            <div style={{ flex: 1 }}>
              <Select
                label={t("credits.run.condition")}
                value={conditionId}
                onChange={(value) => setConditionId(value as string | null)}
                options={settledConditions.map((condition) => ({
                  value: condition.id,
                  label: [
                    condition.owner?.company_name,
                    t(`credits.procurement-type.${condition.procurement_type}`),
                  ]
                    .filter(Boolean)
                    .join(" · "),
                }))}
              />
            </div>
            <Button
              size={"sm"}
              variant={"filled"}
              skin={"success"}
              disabled={conditionId === null || action.isPending}
              onClick={() =>
                action.mutate({
                  url: "/supplier-credit/run",
                  body: { supplier_procurement_condition_id: conditionId },
                })
              }
            >
              {t("credits.run.button")}
            </Button>
          </Flex>
          <TextV2 variant={"body1"} style={{ marginTop: 10 }}>
            {t("credits.run.hint")}
          </TextV2>
        </Panel.Content>
      </Panel>
      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Panel.Icon>
              <span className={"icon-15-basic_info"} />
            </Panel.Icon>
            {t("credits.list.title")}
          </Panel.Title>
        </Panel.Header>
        <Panel.Content>
          {rows.length === 0 ? (
            <TextV2 variant={"body1"}>{t("credits.list.empty")}</TextV2>
          ) : (
            <MuiTable size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("credits.list.date")}</TableCell>
                  <TableCell>{t("credits.list.document")}</TableCell>
                  <TableCell>{t("credits.list.type")}</TableCell>
                  <TableCell>{t("credits.list.procurement-type")}</TableCell>
                  <TableCell align={"right"}>
                    {t("credits.list.orders")}
                  </TableCell>
                  <TableCell align={"right"}>
                    {t("credits.list.gross")}
                  </TableCell>
                  <TableCell>{t("credits.list.email")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((credit) => (
                  <TableRow key={credit.id}>
                    <TableCell>{formatDateTime(credit.created_at)}</TableCell>
                    <TableCell>
                      {credit.document ? (
                        <Button
                          size={"sm"}
                          variant={"outlined"}
                          onClick={() =>
                            api.openTab({
                              resource: ResourceEnum.Document,
                              type: "form",
                              action: FormAction.Edit,
                              id: credit.document?.id ?? "",
                            })
                          }
                        >
                          {credit.document.document_number ?? "—"}
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {credit.document
                        ? t(
                            `credits.document-type.${credit.document.document_type}`,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {t(`credits.procurement-type.${credit.procurement_type}`)}
                    </TableCell>
                    <TableCell align={"right"}>{credit.orders_count}</TableCell>
                    <TableCell align={"right"}>
                      {money(credit.document?.document_total_gross)}
                    </TableCell>
                    <TableCell>
                      {credit.emailed_at
                        ? t("credits.list.emailed", {
                            date: formatDateTime(credit.emailed_at),
                          })
                        : credit.email_error ?? t("credits.list.not-emailed")}
                    </TableCell>
                    <TableCell>
                      <Button
                        size={"sm"}
                        variant={"outlined"}
                        disabled={action.isPending}
                        onClick={() =>
                          action.mutate({
                            url: `/supplier-credit/${credit.id}/send-email`,
                          })
                        }
                      >
                        {t("credits.list.resend")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          )}
        </Panel.Content>
      </Panel>
    </Flex>
  );
};
